import React, { useRef, useState, useEffect } from 'react';
import { Copy } from 'components/components/atoms';
import styled from 'styled-components';
import cx from 'classnames';
import useNewColorSchemaOptions from 'utils/hooks/useNewColorSchemaOptions';
import Calendar from 'components/icons/Calendar';
import { useSpring, animated } from 'react-spring';
import { useMeasure } from 'react-use';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import getMapLink from 'utils/getMapLink';
import OpenNewTab from 'components/icons/OpenNewTab';
import Mail from 'components/icons/Mail';
import CallIcon from 'components/icons/Call';
import MapMarkerIcon from 'components/icons/MapMarker';

const ToggleScheduleContainer = styled.div`
  place-self: center;
`;

const CopyStyled = styled(Copy)`
  overflow-wrap: break-word;
`;

const CallIconContainer = styled.div`
  min-width: 1.875rem;
`;

const ContactHoursSubTitles = styled.h3`
  ${({ showNewSchemaColor, color }) => showNewSchemaColor && `color: ${color};`}
`;
const SalesCenterAddress = styled.div`
  ${({ showNewSchemaColor, color }) => showNewSchemaColor && `color: ${color};`}
`;
const SalesPersonName = styled.p`
  ${({ showNewSchemaColor, color }) => showNewSchemaColor && `color: ${color};`}
`;
const CalendarContainer = styled.div`
  ${({ showNewSchemaColor, textColor, bgColor }) =>
    showNewSchemaColor && `color: ${textColor}; background-color: ${bgColor};`}
`;
const SalesCenterHoursMessage = styled.p`
  ${({ showNewSchemaColor, color }) => showNewSchemaColor && `color: ${color};`}
`;
const ToggleScheduleIcon = styled.svg`
  ${({ showNewSchemaColor, color }) => showNewSchemaColor && `color: ${color};`}
`;
const ScheduleHoursContainer = styled.div`
  ${({ showNewSchemaColor, color }) => showNewSchemaColor && `color: ${color};`}
`;
const UnderLineText = styled.a`
  ${({ showNewSchemaColor, underlineColor }) =>
    showNewSchemaColor && `text-decoration-color: ${underlineColor};`}
`;

const hasHoursOrDisplayText = (day) => {
  return day && (day.displayText || getDayHours(day.hours));
};
const getDayHours = (hours) => {
  if (hours) {
    const hoursSplit = hours.split('-');
    if (hoursSplit.length > 0 && hoursSplit[0].length > 1) return hours;
  }
  return '';
};
const getNote = (ssn) => {
  if (ssn && ssn.date) {
    const date = ssn.date.split('-');
    const text = ssn.text;
    if (date && date.length > 2) {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const dateNote = new Date(date[0], date[1], date[2]);
      dateNote.setHours(0, 0, 0, 0);
      //console.log({ now, dateNote, date });
      if (dateNote >= now) return text;
    }
  }
  return '';
};

const CommunityProductOverviewContactHours = ({
  salesCenterVM,
  salesPersonVM,
}) => {
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);
  const {
    newSchemaColorHexaCode,
    newSchemaTextColorHexaCode,
    showNewSchemaColor,
  } = useNewColorSchemaOptions();
  const salesCenterNoteVM = salesCenterVM.salesCenterNoteVM;
  const defaultHeight = '0px';
  const [contentHeight, setContentHeight] = useState(defaultHeight);
  const [scheduleOpen, toggleScheduleOpen] = useState(false);

  //render Contact & Hours section
  const [scheduleRefHeight, { height }] = useMeasure();
  const getProperHeight = () => {
    if (scheduleOpen) {
      return `${contentHeight}px`;
    }
    return '0px';
  };
  const expand = useSpring({
    height: getProperHeight(),
    pointerEvents: scheduleOpen ? 'all' : 'none',
  });
  const fade = useSpring({
    opacity: scheduleOpen ? 1 : 0,
    delay: scheduleOpen ? 200 : 0,
  });
  useEffect(() => {
    setContentHeight(height);
    window.addEventListener('resize', setContentHeight(height));
    return window.removeEventListener('resize', setContentHeight(height));
  }, [height]);
  const closeScheduleOpenRef = useRef();
  const [isEndOfHours, setIsEndOfHours] = useState(false);
  useEffect(() => {
    const element = closeScheduleOpenRef.current;
    if (isEndOfHours && element) {
      element.focus();
    }
  }, [isEndOfHours]);
  const renderSchedule = () => (
    <animated.div
      className='flex flex-col items-lefy ml-10 justify-start w-full'
      ref={scheduleRefHeight}
      style={fade}
    >
      {salesCenterVM &&
        salesCenterVM.schedule &&
        salesCenterVM.schedule.map((e, i) => {
          if (e.day && (e.hours.length > 5 || e.displayText)) {
            return (
              <div
                className='flex justify-start w-full text-sm font-graphie text-left leading-7'
                key={e.day}
              >
                {hasHoursOrDisplayText(e) && (
                  <>
                    <div className='flex flex-col w-10 mr-3'>
                      <span className='text-left w-10 font-graphie mr-3'>
                        {e.day.substring(0, 3) + ':'}
                      </span>
                    </div>
                    <div className='flex flex-col'>
                      <span className='text-left font-graphie whitespace-no-wrap'>
                        {' ' + getDayHours(e.hours)}
                      </span>
                      <span className='text-left font-graphie whitespace-no-wrap'>
                        {e.displayText ? e.displayText : ' '}
                      </span>
                    </div>
                  </>
                )}
              </div>
            );
          }
          return '';
        })}
    </animated.div>
  );
  const getClosedHour = () => {
    const weekdays = [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ];
    const d = new Date();
    let day = d.getDay();
    const hours =
      salesCenterVM &&
      salesCenterVM.schedule &&
      salesCenterVM.schedule.find((h) => h.day.includes(weekdays[day - 1]));
    const chour = hours && hours.hours && hours.hours.split('-');
    return chour && chour.length > 1 ? chour[1] : '';
  };
  const renderContactHours = () => (
    <div className='flex flex-col md:flex-row mt-2 w-full'>
      <div className='flex flex-col w-full md:w-4/12'>
        {!isMobile &&
          salesCenterVM.title &&
          salesCenterVM.address1 &&
          salesCenterVM.city &&
          salesCenterVM.stateAbbreviation &&
          salesCenterVM.zipcode && (
            <>
              <ContactHoursSubTitles
                className={cx(
                  'mb-4 font-trade-gothic-20 font-bold leading-7 uppercase text-xl ',
                  {
                    ' text-mattamy-blue': !showNewSchemaColor,
                  },
                )}
                showNewSchemaColor={showNewSchemaColor}
                color={newSchemaTextColorHexaCode}
              >
                {salesCenterVM.title}
              </ContactHoursSubTitles>
              <UnderLineText
                className='underline'
                showNewSchemaColor={showNewSchemaColor}
                underlineColor={newSchemaTextColorHexaCode}
                href={
                  salesCenterVM &&
                  salesCenterVM.gmbLink &&
                  salesCenterVM.gmbLink.href
                    ? salesCenterVM.gmbLink.href
                    : salesCenterVM &&
                      salesCenterVM.latitude &&
                      salesCenterVM.longitude
                    ? getMapLink(
                        salesCenterVM.latitude + ',' + salesCenterVM.longitude,
                      )
                    : getMapLink(
                        salesCenterVM.address1 +
                          ' ' +
                          salesCenterVM.address2 +
                          ' ' +
                          salesCenterVM.city +
                          ', ' +
                          salesCenterVM.stateAbbreviation +
                          ' ' +
                          salesCenterVM.zipcode,
                      )
                }
                target='_blank'
                rel='noopener noreferrer'
                aria-label={
                  'Go to ' +
                  salesCenterVM.address1 +
                  ' ' +
                  salesCenterVM.address2 +
                  ' ' +
                  salesCenterVM.city +
                  ', ' +
                  salesCenterVM.stateAbbreviation +
                  ' ' +
                  salesCenterVM.zipcode +
                  '. Opens in GoogleMaps'
                }
              >
                <SalesCenterAddress
                  className={cx(
                    'flex flex-col w-full text-normal mb-10-mattamy font-graphie text-left leading-7',
                    {
                      ' text-mattamy-blue': !showNewSchemaColor,
                    },
                  )}
                  showNewSchemaColor={showNewSchemaColor}
                  color={newSchemaTextColorHexaCode}
                >
                  <span>
                    {salesCenterVM.address1 + ' ' + salesCenterVM.address2}
                  </span>
                  <span className=' flex flex-row'>
                    {(salesCenterVM.city && salesCenterVM.city.length > 0
                      ? salesCenterVM.city
                      : '') +
                      ', ' +
                      salesCenterVM.stateAbbreviation +
                      ' ' +
                      (salesCenterVM.zipcode && salesCenterVM.zipcode.length > 0
                        ? salesCenterVM.zipcode
                        : '')}
                    {<OpenNewTab className='w-4 ml-1' />}
                  </span>
                </SalesCenterAddress>
              </UnderLineText>
              {salesCenterVM.phoneNumberCall && (
                <UnderLineText
                  className='underline'
                  showNewSchemaColor={showNewSchemaColor}
                  underlineColor={newSchemaTextColorHexaCode}
                  href={`tel:${salesCenterVM.phoneNumberCall}`}
                  aria-label={'Call to ' + salesCenterVM.phoneNumberCall}
                >
                  <Copy
                    color={
                      showNewSchemaColor ? newSchemaTextColorHexaCode : 'blue'
                    }
                    className='w-full font-graphie text-left leading-7'
                  >
                    {salesCenterVM.phoneNumberCall}
                  </Copy>
                </UnderLineText>
              )}
              {salesCenterVM.email && (
                <UnderLineText
                  className='underline'
                  showNewSchemaColor={showNewSchemaColor}
                  underlineColor={newSchemaTextColorHexaCode}
                  href={`mailto:${salesCenterVM.email}`}
                  aria-label={'Email to ' + salesCenterVM.email}
                >
                  <Copy
                    color={
                      showNewSchemaColor ? newSchemaTextColorHexaCode : 'blue'
                    }
                    className='w-full font-graphie text-left leading-7'
                  >
                    {salesCenterVM.email}
                  </Copy>
                </UnderLineText>
              )}
            </>
          )}
      </div>
      <div className='flex flex-col w-full mt-8 md:mt-0 md:w-4/12'>
        {!isMobile &&
          salesPersonVM.title &&
          (salesPersonVM.email || salesPersonVM.phoneNumberCall) && (
            <>
              <ContactHoursSubTitles
                className={cx(
                  'mb-4 font-trade-gothic-20 font-bold leading-7 uppercase text-xl ',
                  {
                    ' text-mattamy-blue': !showNewSchemaColor,
                  },
                )}
                showNewSchemaColor={showNewSchemaColor}
                color={newSchemaTextColorHexaCode}
              >
                {salesPersonVM.title}
              </ContactHoursSubTitles>
              {salesPersonVM.displayPersonName === 'true' && (
                <SalesPersonName
                  className={cx(
                    'w-full font-graphie text-left leading-7 font-bold',
                    {
                      ' text-mattamy-blue': !showNewSchemaColor,
                    },
                  )}
                  showNewSchemaColor={showNewSchemaColor}
                  color={newSchemaTextColorHexaCode}
                >
                  {salesPersonVM.personName}
                </SalesPersonName>
              )}
              {salesPersonVM.email && (
                <UnderLineText
                  className='underline'
                  showNewSchemaColor={showNewSchemaColor}
                  underlineColor={newSchemaTextColorHexaCode}
                  href={`mailto:${salesPersonVM.email}`}
                  aria-label={'Email to ' + salesPersonVM.email}
                >
                  <CopyStyled
                    color={
                      showNewSchemaColor ? newSchemaTextColorHexaCode : 'blue'
                    }
                    className='w-full font-graphie text-left leading-7'
                  >
                    {salesPersonVM.email}
                  </CopyStyled>
                </UnderLineText>
              )}
              {salesPersonVM.phoneNumberCall && (
                <UnderLineText
                  className='underline'
                  showNewSchemaColor={showNewSchemaColor}
                  underlineColor={newSchemaTextColorHexaCode}
                  href={`tel:${salesPersonVM.phoneNumberCall}`}
                  aria-label={'Call to ' + salesPersonVM.phoneNumberCall}
                >
                  <Copy
                    color={
                      showNewSchemaColor ? newSchemaTextColorHexaCode : 'blue'
                    }
                    className='w-full font-graphie text-left leading-7'
                  >
                    {salesPersonVM.phoneNumberCall}
                  </Copy>
                </UnderLineText>
              )}
            </>
          )}
      </div>
      {isMobile && (
        <div className='flex flex-col w-full'>
          <ContactHoursSubTitles
            className={cx(
              'mb-4 font-trade-gothic-20 font-bold leading-7 uppercase text-xl ',
              {
                ' text-mattamy-blue': !showNewSchemaColor,
              },
            )}
            showNewSchemaColor={showNewSchemaColor}
            color={newSchemaTextColorHexaCode}
          >
            {salesCenterVM.title}
          </ContactHoursSubTitles>

          <div className='grid grid-cols-2 gap-4'>
            {salesCenterVM.address1 && (
              <div
                className={cx('flex font-graphie rounded py-4 justify-left', {
                  ' bg-mattamy-blue text-white': !showNewSchemaColor,
                  ' bg-white text-black': showNewSchemaColor,
                })}
              >
                <div
                  tabIndex={-1}
                  aria-hidden='true'
                  //white={white}
                  className={cx('rounded-full  ml-4 mr-2', {
                    'bg-white': !showNewSchemaColor,
                    'bg-black': showNewSchemaColor,
                  })}
                >
                  <MapMarkerIcon
                    className={cx('w-8 h-8 p-1 ', {
                      'text-mattamy-blue ': !showNewSchemaColor,
                      'text-white': showNewSchemaColor,
                    })}
                  />
                </div>
                <div className={cx('flex flex-col', {})}>
                  <a
                    className='mt-1'
                    href={
                      salesCenterVM &&
                      salesCenterVM.gmbLink &&
                      salesCenterVM.gmbLink.href
                        ? salesCenterVM.gmbLink.href
                        : salesCenterVM &&
                          salesCenterVM.latitude &&
                          salesCenterVM.longitude
                        ? getMapLink(
                            salesCenterVM.latitude +
                              ',' +
                              salesCenterVM.longitude,
                          )
                        : getMapLink(
                            salesCenterVM.address1 +
                              ' ' +
                              salesCenterVM.address2 +
                              ' ' +
                              salesCenterVM.city +
                              ', ' +
                              salesCenterVM.stateAbbreviation +
                              ' ' +
                              salesCenterVM.zipcode,
                          )
                    }
                    target='_blank'
                    rel='noopener noreferrer'
                    aria-label={
                      'Go to ' +
                      salesCenterVM.address1 +
                      ' ' +
                      salesCenterVM.address2 +
                      ' ' +
                      salesCenterVM.city +
                      ', ' +
                      salesCenterVM.stateAbbreviation +
                      ' ' +
                      salesCenterVM.zipcode +
                      '. Opens in GoogleMaps'
                    }
                  >
                    Directions
                  </a>
                </div>
              </div>
            )}
            {salesCenterVM.phoneNumberCall && (
              <div
                className={cx(
                  'flex items-center break-all rounded py-4 justify-left font-graphie',
                  {
                    ' bg-mattamy-blue text-white': !showNewSchemaColor,
                    ' bg-white text-black': showNewSchemaColor,
                  },
                )}
              >
                <CallIconContainer
                  tabIndex={-1}
                  aria-hidden='true'
                  className='rounded-full ml-4 mr-2'
                >
                  <CallIcon className='w-30-mattamy h-30-mattamy' />
                </CallIconContainer>
                <a
                  //className="underline"
                  href={`tel:${salesCenterVM.phoneNumberCall}`}
                  aria-label={'Call to ' + salesCenterVM.phoneNumberCall}
                >
                  {'Call'}
                </a>
              </div>
            )}
            {salesCenterVM.email && (
              <div
                className={cx(
                  'flex items-center font-graphie break-all rounded py-4 justify-left',
                  {
                    ' bg-mattamy-blue text-white': !showNewSchemaColor,
                    ' bg-white text-black': showNewSchemaColor,
                  },
                )}
              >
                <div
                  className={cx(
                    'ml-4 flex justify-center font-graphie items-center w-30-mattamy h-30-mattamy inline mr-2 rounded-full',
                    {
                      ' bg-white text-mattamy-blue': !showNewSchemaColor,
                      ' bg-black text-white': showNewSchemaColor,
                    },
                  )}
                >
                  <Mail className='h-4 w-4' />
                </div>
                <a
                  href={`mailto:${salesCenterVM.email}`}
                  aria-label={'Email to ' + salesCenterVM.email}
                >
                  {'Email'}
                </a>
              </div>
            )}
          </div>
          <ContactHoursSubTitles
            className={cx(
              'mb-4 font-trade-gothic-20 font-bold leading-7 uppercase text-xl mt-8',
              {
                ' text-mattamy-blue': !showNewSchemaColor,
              },
            )}
            showNewSchemaColor={showNewSchemaColor}
            color={newSchemaTextColorHexaCode}
          >
            {salesPersonVM.title}
          </ContactHoursSubTitles>
          <div className='grid grid-cols-2 gap-4'>
            {salesPersonVM.email && (
              <div
                className={cx(
                  'flex items-center font-graphie break-all rounded py-4 justify-left',
                  {
                    ' bg-mattamy-blue text-white': !showNewSchemaColor,
                    ' bg-white text-black': showNewSchemaColor,
                  },
                )}
              >
                <div
                  className={cx(
                    'ml-4 flex justify-center font-graphie items-center w-30-mattamy h-30-mattamy inline mr-2 rounded-full',
                    {
                      ' bg-white text-mattamy-blue': !showNewSchemaColor,
                      ' bg-black text-white': showNewSchemaColor,
                    },
                  )}
                >
                  <Mail className='h-4 w-4' />
                </div>
                <a
                  href={`mailto:${salesPersonVM.email}`}
                  aria-label={'Email to ' + salesPersonVM.email}
                >
                  {'Email'}
                </a>
              </div>
            )}
            {salesPersonVM.phoneNumberCall && (
              <div
                className={cx(
                  'flex items-center font-graphie break-all rounded py-4 justify-left',
                  {
                    ' bg-mattamy-blue text-white': !showNewSchemaColor,
                    ' bg-white text-black': showNewSchemaColor,
                  },
                )}
              >
                <CallIconContainer
                  tabIndex={-1}
                  aria-hidden='true'
                  className='rounded-full ml-4 mr-2'
                >
                  <CallIcon className='w-30-mattamy h-30-mattamy' />
                </CallIconContainer>
                <a
                  //className="underline"
                  href={`tel:${salesPersonVM.phoneNumberCall}`}
                  aria-label={'Call to ' + salesPersonVM.phoneNumberCall}
                >
                  {'Call'}
                </a>
              </div>
            )}
          </div>
        </div>
      )}
      <div className='flex flex-col w-full mt-8 md:mt-0 md:w-4/12'>
        <ContactHoursSubTitles
          className={cx(
            'mb-4 font-trade-gothic-20 font-bold leading-7 uppercase text-xl ',
            {
              ' text-mattamy-blue': !showNewSchemaColor,
            },
          )}
          showNewSchemaColor={showNewSchemaColor}
          color={newSchemaTextColorHexaCode}
        >
          {salesCenterVM.scheduleTitle}
        </ContactHoursSubTitles>

        {salesCenterVM &&
          salesCenterVM.schedule &&
          salesCenterVM.schedule.length > 0 && (
            <div className='font-graphie text-base leading-relaxed md:leading-5  md:mb-0 flex items-start'>
              <CalendarContainer
                className={cx(
                  'flex justify-center items-center w-30-mattamy h-30-mattamy inline mr-2 rounded-full w-7',
                  {
                    ' bg-mattamy-blue text-white': !showNewSchemaColor,
                  },
                )}
                showNewSchemaColor={showNewSchemaColor}
                textColor={newSchemaColorHexaCode}
                bgColor={newSchemaTextColorHexaCode}
              >
                <Calendar className='h-4 w-4' />
              </CalendarContainer>

              <ToggleScheduleContainer
                className='flex justify-center items-center place-self-center'
                aria-label={scheduleOpen ? 'Close Schedule' : 'Show Schedule'}
                aria-expanded={scheduleOpen}
                as={
                  salesCenterVM &&
                  salesCenterVM.schedule &&
                  salesCenterVM.schedule.length > 0
                    ? 'button'
                    : 'span'
                }
                onClick={() => {
                  toggleScheduleOpen(!scheduleOpen);
                }}
                onFocus={(e) => {
                  setIsEndOfHours(false);
                }}
                ref={closeScheduleOpenRef}
              >
                <SalesCenterHoursMessage
                  className={cx('break-all ', {
                    ' text-mattamy-blue': !showNewSchemaColor,
                  })}
                  showNewSchemaColor={showNewSchemaColor}
                  color={newSchemaTextColorHexaCode}
                >
                  {salesCenterVM.openNow ? (
                    <>
                      <span className='font-bold'>{'Open Now - '}</span>
                      <span>{'Closes at ' + getClosedHour()}</span>
                    </>
                  ) : (
                    <span className='font-bold'>{'Closed Now'}</span>
                  )}
                </SalesCenterHoursMessage>
                {salesCenterVM &&
                  salesCenterVM.schedule &&
                  salesCenterVM.schedule.length > 0 && (
                    <ToggleScheduleIcon
                      className={cx('w-6 h-6 transform mt-1', {
                        'rotate-90': scheduleOpen,
                        '-rotate-90': !scheduleOpen,
                        'text-mattamy-blue': !showNewSchemaColor,
                      })}
                      viewBox='0 0 24 24'
                      fill='none'
                      showNewSchemaColor={showNewSchemaColor}
                      color={newSchemaTextColorHexaCode}
                    >
                      <path
                        fill='currentColor'
                        d='M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z'
                      />
                    </ToggleScheduleIcon>
                  )}
              </ToggleScheduleContainer>
            </div>
          )}
        <ScheduleHoursContainer
          className={cx(
            'font-graphie text-base leading-relaxed md:leading-5 md:mb-0 flex items-start',
            {
              'text-mattamy-blue': !showNewSchemaColor,
            },
          )}
          showNewSchemaColor={showNewSchemaColor}
          color={newSchemaTextColorHexaCode}
        >
          <div className='w-full  z-20'>
            {scheduleOpen && (
              <>
                <animated.div style={expand}>{renderSchedule()}</animated.div>
                <div
                  onFocus={(e) => {
                    setIsEndOfHours(true);
                  }}
                >
                  {' '}
                </div>
              </>
            )}
          </div>
        </ScheduleHoursContainer>
      </div>
    </div>
  );

  return (
    <>
      <div className='w-full text-normal mb-10-mattamy font-graphie text-left leading-7 text-mattamy-blue mt-4'>
        {getNote(salesCenterNoteVM)}
      </div>
      {renderContactHours()}
    </>
  );
};

export default CommunityProductOverviewContactHours;
